<script setup lang="ts">
  import { ref, computed, watch } from 'vue'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import { User, Building2, Landmark } from 'lucide-vue-next'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import { type Option } from '@/components/admin/form/SelectField.vue'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    RelationshipTypeEnum,
    EntityTypeEnum,
    type CustomerEntityWithRelationship as EntityWithRelationship
  } from '@/types/generated'
  import { EntitiesService } from '@/types/generated'
  import { useToast } from '@/components/ui/toast'
  import { formatEntityName as formatEntityNameUtil } from '@/utils/entityFormatting'

  const { t } = useTranslation({
    en: {
      searchEntities: 'Search entities...',
      noEntitiesAvailable: 'No entities available for replacement.',
      cancel: 'Cancel',
      confirm: 'Confirm',
      addClientTitle: 'Set New Client',
      removeClientTitle: 'Remove Client',
      addOpponentTitle: 'Set as Opponent',
      noName: 'Unnamed',
      removeClientMessageLine1: 'The client role will be removed from',
      removeClientMessageLine2: '.',
      removeClientMessageLine3: 'Please select the new client.',
      addOpponentMessageLine1: 'will be set as new opponent.',
      addOpponentMessageLine2: 'Please select the new client.',
      addClientChangeFrom: 'You are about to change the client from',
      addClientChangeTo: 'to',
      addClientChangeEnd: '.',
      newClientLabel: 'New Client',
      error: 'Error',
      genericError: 'An error occurred while updating the relationship.'
    },
    de: {
      searchEntities: 'Entitäten suchen...',
      noEntitiesAvailable: 'Keine Entitäten für den Ersatz verfügbar.',
      cancel: 'Abbrechen',
      confirm: 'Bestätigen',
      addClientTitle: 'Neuen Mandanten festlegen',
      removeClientTitle: 'Mandanten entfernen',
      addOpponentTitle: 'Als Gegner festlegen',
      noName: 'Unbenannt',
      removeClientMessageLine1: 'Die Mandant Rolle wird von',
      removeClientMessageLine2: 'entfernt.',
      removeClientMessageLine3: 'Bitte, wählen Sie den neuen Mandanten aus.',
      addOpponentMessageLine1: 'wird als neuer Gegner festgelegt.',
      addOpponentMessageLine2: 'Bitte, wählen Sie den neuen Mandanten aus.',
      addClientChangeFrom: 'Sie sind dabei den Mandanten von',
      addClientChangeTo: 'zu',
      addClientChangeEnd: 'zu wechseln.',
      newClientLabel: 'Neuer Mandant',
      error: 'Fehler',
      genericError: 'Beim Aktualisieren der Beziehung ist ein Fehler aufgetreten.'
    }
  })

  interface Props {
    isOpen: boolean
    action: 'add' | 'remove' | 'addOpponent'
    relationshipUuid?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    relationshipUuid: ''
  })

  const { toast } = useToast()
  const emit = defineEmits(['update:isOpen', 'relationshipUpdated'])

  const caseStore = useCaseStore()
  const caseRecord = computed(() => caseStore.caseRecord)

  const newClientEntity = ref<EntityWithRelationship | null>(null)
  const isSubmitting = ref(false)

  const selectedEntityRelationship = computed<EntityWithRelationship | undefined>(() => {
    return caseRecord.value?.data?.entities.find((entity) => {
      return entity.uuid === props.relationshipUuid
    })
  })

  const currentClientEntity = computed<EntityWithRelationship | undefined>(() => {
    return caseRecord.value?.data?.entities?.find((entity) => {
      return entity.relationships.some(
        (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
      )
    })
  })

  const availableEntities = computed<EntityWithRelationship[]>(() => {
    if (!caseRecord.value?.data?.entities) return []

    return caseRecord.value.data.entities.filter((entity) => {
      if (entity.uuid === props.relationshipUuid) return false

      const hasClientRelationship = entity.relationships.some(
        (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
      )

      return !hasClientRelationship
    })
  })

  const formatEntityName = (entity: EntityWithRelationship) => {
    return formatEntityNameUtil(entity.entity, t('noName'))
  }

  const entityOptions = computed<Option[]>(() => {
    return availableEntities.value.map((entity) => ({
      value: entity.uuid,
      title: formatEntityName(entity),
      icon:
        entity.entity.entity_type === EntityTypeEnum.NATURAL_PERSON
          ? User
          : entity.entity.entity_type === EntityTypeEnum.LEGAL_PERSON
            ? Building2
            : Landmark
    })) as Option[]
  })

  const updateOpen = (value: boolean) => {
    newClientEntity.value = null
    emit('update:isOpen', value)
  }

  const close = () => {
    newClientEntity.value = null
    emit('update:isOpen', false)
  }

  watch(
    [selectedEntityRelationship, () => caseRecord.value?.data?.entities],
    ([relationship, entities]) => {
      if (props.isOpen && (!entities || !relationship)) {
        close()
      }
    }
  )

  watch(
    [selectedEntityRelationship, () => props.action, () => props.isOpen],
    ([newSelectedEntityRelationship, newAction, isOpen]) => {
      if (!isOpen) return

      newClientEntity.value = null
      if (newAction === 'add') {
        newClientEntity.value = newSelectedEntityRelationship || null
      }
    },
    { immediate: true }
  )

  const selectNewClientEntity = async (entity?: EntityWithRelationship) => {
    if (!entity) return
    newClientEntity.value = entity
  }

  const handleConfirm = async () => {
    if (!newClientEntity.value) return

    isSubmitting.value = true
    try {
      if (caseStore.isCreatingCase) {
        caseStore.switchClientDuringCreation(
          newClientEntity.value.entity.uuid,
          props.action,
          selectedEntityRelationship.value?.entity.uuid
        )

        emit('relationshipUpdated', {})
        close()
        return
      }

      if (!caseRecord.value?.data?.uuid) return

      if (props.action === 'addOpponent') {
        if (
          !selectedEntityRelationship.value ||
          !selectedEntityRelationship.value.uuid ||
          !newClientEntity.value ||
          !newClientEntity.value.uuid
        ) {
          return
        }

        const response = await EntitiesService.entitiesConvertClientToOpponentWithNewClientCreate({
          requestBody: {
            old_client_uuid: selectedEntityRelationship.value.uuid,
            new_client_uuid: newClientEntity.value.uuid,
            case_data_uuid: caseRecord.value.data.uuid
          }
        })

        // Update local state with the response
        selectedEntityRelationship.value.relationships = response.old_client.relationships
        newClientEntity.value.relationships = response.new_client.relationships

        emit('relationshipUpdated', response)
      } else {
        const response = await EntitiesService.entitiesSwitchUniqueCaseDataRelationshipCreate({
          requestBody: {
            new_entity_relationship_which_should_hold_the_designated_relationship_uuid:
              newClientEntity.value.uuid!,
            case_data_uuid: caseRecord.value.data.uuid,
            relationship_type: RelationshipTypeEnum.ENTITIES_CLIENT
          }
        })

        newClientEntity.value.relationships =
          response.new_entity_relationship_which_should_hold_the_designated_relationship.relationships
        if (currentClientEntity.value) {
          currentClientEntity.value.relationships =
            response.entity_relationship_currently_holding_the_designated_relationship.relationships
        }

        emit('relationshipUpdated', response)
      }
      close()
    } catch (error) {
      toast({
        title: t('error'),
        description: t('genericError'),
        variant: 'destructive'
      })
    } finally {
      isSubmitting.value = false
    }
  }

  const isConfirmDisabled = computed(() => {
    if (isSubmitting.value) return true
    if (!newClientEntity.value) return true
    return false
  })
</script>

<template>
  <Dialog
    :open="isOpen"
    @update:open="updateOpen"
  >
    <DialogContent class="sm:max-w-[500px]">
      <DialogHeader>
        <DialogTitle>
          {{
            props.action === 'add'
              ? t('addClientTitle')
              : props.action === 'addOpponent'
                ? t('addOpponentTitle')
                : t('removeClientTitle')
          }}
        </DialogTitle>
        <DialogDescription></DialogDescription>
      </DialogHeader>
      <div class="space-y-4">
        <template v-if="props.action === 'add'">
          <p class="text-sm text-neutral-800">
            {{ t('addClientChangeFrom') }}
            <strong>{{ currentClientEntity ? formatEntityName(currentClientEntity) : '' }}</strong>
            {{ t('addClientChangeTo') }}
            <strong>
              {{ selectedEntityRelationship ? formatEntityName(selectedEntityRelationship) : '' }}
            </strong>
            {{ t('addClientChangeEnd') }}
          </p>
        </template>
        <template v-else-if="props.action === 'addOpponent'">
          <div class="text-sm text-neutral-800">
            <strong>
              {{ selectedEntityRelationship ? formatEntityName(selectedEntityRelationship) : '' }}
            </strong>
            {{ t('addOpponentMessageLine1') }}
            <br />
            {{ t('addOpponentMessageLine2') }}
          </div>
          <div class="space-y-2">
            <label class="text-sm font-medium leading-none peer-disabled:opacity-70">
              {{ t('newClientLabel') }}
            </label>
            <SelectField
              v-if="availableEntities.length > 0"
              name="client-selection"
              :placeholder="t('searchEntities')"
              :options="entityOptions"
              :model-value="newClientEntity?.uuid || null"
              @update:model-value="
                (value) =>
                  selectNewClientEntity(availableEntities.find((entity) => entity.uuid === value))
              "
            />
            <p
              v-else
              class="text-sm text-neutral-800"
            >
              {{ t('noEntitiesAvailable') }}
            </p>
          </div>
        </template>
        <template v-else>
          <div class="text-sm text-neutral-800">
            {{ t('removeClientMessageLine1') }}
            <strong>{{ currentClientEntity ? formatEntityName(currentClientEntity) : '' }}</strong>
            {{ t('removeClientMessageLine2') }}
            <br />
            {{ t('removeClientMessageLine3') }}
          </div>
          <div class="space-y-2">
            <label class="text-sm font-medium leading-none peer-disabled:opacity-70">
              {{ t('newClientLabel') }}
            </label>
            <SelectField
              v-if="availableEntities.length > 0"
              name="client-selection"
              :placeholder="t('searchEntities')"
              :options="entityOptions"
              :model-value="newClientEntity?.uuid || null"
              @update:model-value="
                (value) =>
                  selectNewClientEntity(availableEntities.find((entity) => entity.uuid === value))
              "
            />
            <p
              v-else
              class="text-sm text-neutral-800"
            >
              {{ t('noEntitiesAvailable') }}
            </p>
          </div>
        </template>
      </div>
      <DialogFooter class="gap-2">
        <Button
          variant="secondary"
          class="flex-1"
          :disabled="isSubmitting"
          @click="close"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          class="flex-1"
          :disabled="isConfirmDisabled"
          @click="handleConfirm"
        >
          <span
            v-if="isSubmitting"
            class="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
          ></span>
          {{ t('confirm') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
